import { useQuery } from '@tanstack/react-query';
import { createContext, type ReactNode, useContext, useEffect } from 'react';

import { ViteEnv } from '@/env';
import { projectQueries } from '@/lib/queries/project';

import { supabaseClient } from '../../lib/supabase';

class WebSocketService {
  private ws: WebSocket | null = null;
  private messageHandlers: Map<string, (args: unknown) => void> = new Map();
  private url: string = ViteEnv.VITE_WS_URL;
  private reconnect: boolean = false;

  async connect() {
    // Get the JWT token from wherever you store it (localStorage, cookie, etc.)
    const {
      data: { session },
    } = await supabaseClient.auth.getSession();

    // Append token as query parameter
    const wsUrl = `${this.url}?token=${session?.access_token}`;
    // Or you can use a custom protocol
    // this.ws = new WebSocket(this.url, [`token-${token}`]);

    this.ws = new WebSocket(wsUrl);

    this.ws.onmessage = event => {
      const message = JSON.parse(event.data);
      this.messageHandlers.forEach((handler, type) => {
        if (message.type === type) {
          handler(message);
        }
      });
    };

    this.ws.onclose = event => {
      if (event.code === 4001) {
        console.error('Authentication failed');

        // Handle authentication failure (redirect to login, etc.)
        return;
      }

      if (this.reconnect) {
        setTimeout(() => this.connect(), 1000);
      }
    };
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  addMessageHandler(type: string, handler: (args: any) => void) {
    this.messageHandlers.set(type, handler);
  }

  removeMessageHandler(type: string) {
    this.messageHandlers.delete(type);
  }

  reset() {
    this.reconnect = true;
  }

  async disconnect() {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.reconnect = false;
  }
}

const WebSocketsContext = createContext<WebSocketService | null>(null);

const wsService = new WebSocketService();

export const WebSocketsProvider = ({
  children,
}: {
  children: ReactNode;
}): JSX.Element => {
  const { data: defaultProject } = useQuery(projectQueries.default());

  useEffect(() => {
    if (defaultProject?.defaultProject?.id === undefined) {
      return () => {
        wsService.disconnect();
      };
    }

    wsService.reset();
    wsService.connect();

    return () => {
      wsService.disconnect();
    };
  }, [defaultProject]);

  return (
    <WebSocketsContext.Provider value={wsService}>
      {children}
    </WebSocketsContext.Provider>
  );
};

export const useWebSockets = (): {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  addMessageHandler: (type: string, handler: (args: any) => void) => void;
  removeMessageHandler: (type: string) => void;
} => {
  const context = useContext(WebSocketsContext);
  if (context === null) {
    throw new Error('useWebSockets must be used within a WebSocketsProvider');
  }

  return {
    addMessageHandler: context.addMessageHandler.bind(context),
    removeMessageHandler: context.removeMessageHandler.bind(context),
  };
};
